import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { UserPlus, Trash2, Edit2, Save, X, Upload, Search, Filter, BarChart3, Download, FileDown } from 'lucide-react';
import './Subscribers.css';

const Subscribers = () => {
  const [activeTab, setActiveTab] = useState('subscribers');
  const [subscribers, setSubscribers] = useState([]);
  const [genres, setGenres] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ email: '', firstName: '', lastName: '', preferredGenres: [] });
  const [editData, setEditData] = useState({});
  const [message, setMessage] = useState('');
  const [bulkEmails, setBulkEmails] = useState('');
  const [bulkMode, setBulkMode] = useState('subscribers'); // 'subscribers' or 'genres'
  const [bulkGenres, setBulkGenres] = useState('');
  
  // Pagination & filters
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [genreFilter, setGenreFilter] = useState('');
  const [genreScoreThreshold, setGenreScoreThreshold] = useState(0);
  const [minDownloads, setMinDownloads] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // CSV upload
  const [csvFile, setCsvFile] = useState(null);

  // Details modal
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [subscriberDetails, setSubscriberDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    if (activeTab === 'subscribers') {
      fetchSubscribers();
    } else if (activeTab === 'analytics') {
      fetchAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentPage, search, sortBy, sortOrder, genreFilter, genreScoreThreshold, minDownloads]);

  useEffect(() => {
    fetchGenres();
  }, []);

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 50,
        search,
        sortBy,
        sortOrder,
        ...(genreFilter && { genreFilter }),
        ...(genreScoreThreshold > 0 && { genreScoreThreshold }),
        ...(minDownloads > 0 && { minDownloads })
      });

      const response = await api.get(`/subscribers?${params}`);
      setSubscribers(response.data.subscribers);
      setTotalPages(response.data.totalPages);
      setTotalCount(response.data.total);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      setMessage('Error fetching subscribers');
    } finally {
      setLoading(false);
    }
  };

  const fetchGenres = async () => {
    try {
      const response = await api.get('/genres');
      setGenres(response.data.genres || []);
    } catch (error) {
      console.error('Error fetching genres:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/subscriber-analytics');
      setAnalytics(response.data.analytics);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setMessage('');

    try {
      await api.post('/subscribers/subscribe', formData);
      setMessage('Subscriber added successfully');
      setFormData({ email: '', firstName: '', lastName: '', preferredGenres: [] });
      setShowAddForm(false);
      fetchSubscribers();
    } catch (error) {
      setMessage('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleBulkImport = async () => {
    if (bulkMode === 'subscribers') {
      if (!bulkEmails.trim()) {
        setMessage('Please enter email addresses');
        return;
      }

      const lines = bulkEmails.split('\n').filter(e => e.trim());
      const subscribersData = lines.map(line => {
        const parts = line.split('|').map(p => p.trim());
        return {
          email: parts[0] || '',
          firstName: parts[1] || '',
          lastName: parts[2] || '',
          genre: parts[3] || ''
        };
      });

      try {
        const response = await api.post('/subscribers/import', { subscribers: subscribersData });
        const results = response.data.results;
        let msg = `Import completed: ${results.added} added, ${results.skipped} skipped`;
        if (results.genresCreated.length > 0) {
          msg += `. Created genres: ${results.genresCreated.join(', ')}`;
        }
        setMessage(msg);
        setBulkEmails('');
        fetchSubscribers();
      } catch (error) {
        setMessage('Error importing: ' + (error.response?.data?.message || error.message));
      }
    } else if (bulkMode === 'genres') {
      if (!bulkGenres.trim()) {
        setMessage('Please enter genre names');
        return;
      }

      const genreLines = bulkGenres.split('\n').filter(line => line.trim());
      const genreNames = genreLines.map(line => line.trim());

      try {
        const response = await api.post('/genres/bulk-import', { genres: genreNames });
        const results = response.data.results;
        let msg = `Genre import completed: ${results.added} added, ${results.skipped} skipped`;
        if (results.genresAdded.length > 0) {
          msg += `. Added: ${results.genresAdded.join(', ')}`;
        }
        if (results.genresSkipped.length > 0) {
          msg += `. Skipped (already exist): ${results.genresSkipped.join(', ')}`;
        }
        setMessage(msg);
        setBulkGenres('');
        fetchGenres(); // Refresh the genre list
      } catch (error) {
        setMessage('Error importing genres: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleCSVUpload = async () => {
    if (!csvFile) {
      setMessage('Please select a CSV file');
      return;
    }

    const formData = new FormData();
    formData.append('csv', csvFile);

    try {
      // Parse CSV on frontend
      const text = await csvFile.text();
      const lines = text.split('\n');
      const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
      
      const subscribersData = [];
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        
        const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        const subscriber = {};
        
        headers.forEach((header, index) => {
          if (header === 'email') subscriber.email = values[index];
          else if (header === 'firstname' || header === 'first name') subscriber.firstName = values[index];
          else if (header === 'lastname' || header === 'last name') subscriber.lastName = values[index];
          else if (header === 'genre') subscriber.genre = values[index];
        });
        
        if (subscriber.email) {
          subscribersData.push(subscriber);
        }
      }

      const response = await api.post('/subscribers/import', { subscribers: subscribersData });
      const results = response.data.results;
      let msg = `CSV import completed: ${results.added} added, ${results.skipped} skipped`;
      if (results.genresCreated.length > 0) {
        msg += `. Created genres: ${results.genresCreated.join(', ')}`;
      }
      setMessage(msg);
      setCsvFile(null);
      fetchSubscribers();
    } catch (error) {
      setMessage('Error importing CSV: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subscriber?')) {
      return;
    }

    try {
      await api.delete(`/subscribers/${id}`);
      setMessage('Subscriber deleted successfully');
      fetchSubscribers();
    } catch (error) {
      setMessage('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (subscriber) => {
    setEditingId(subscriber._id);
    setEditData({
      firstName: subscriber.firstName || '',
      lastName: subscriber.lastName || '',
      isActive: subscriber.isActive,
      preferredGenres: subscriber.preferredGenres?.map(g => g._id) || []
    });
  };

  const handleSave = async (id) => {
    try {
      await api.put(`/subscribers/${id}`, editData);
      setMessage('Subscriber updated successfully');
      setEditingId(null);
      fetchSubscribers();
    } catch (error) {
      setMessage('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const toggleGenreSelection = (genreId, isEditForm = false) => {
    if (isEditForm) {
      const current = editData.preferredGenres || [];
      if (current.includes(genreId)) {
        setEditData({ ...editData, preferredGenres: current.filter(id => id !== genreId) });
      } else {
        setEditData({ ...editData, preferredGenres: [...current, genreId] });
      }
    } else {
      const current = formData.preferredGenres || [];
      if (current.includes(genreId)) {
        setFormData({ ...formData, preferredGenres: current.filter(id => id !== genreId) });
      } else {
        setFormData({ ...formData, preferredGenres: [...current, genreId] });
      }
    }
  };

  const toggleAllGenres = (isEditForm = false) => {
    if (isEditForm) {
      const current = editData.preferredGenres || [];
      if (current.length === genres.length) {
        // If all are selected, deselect all
        setEditData({ ...editData, preferredGenres: [] });
      } else {
        // Select all genres
        setEditData({ ...editData, preferredGenres: genres.map(g => g._id) });
      }
    } else {
      const current = formData.preferredGenres || [];
      if (current.length === genres.length) {
        // If all are selected, deselect all
        setFormData({ ...formData, preferredGenres: [] });
      } else {
        // Select all genres
        setFormData({ ...formData, preferredGenres: genres.map(g => g._id) });
      }
    }
  };

  const getGenreScorePercentages = (subscriber) => {
    if (!subscriber.genreScore || Object.keys(subscriber.genreScore).length === 0) {
      return [];
    }

    const total = Object.values(subscriber.genreScore).reduce((sum, count) => sum + count, 0);
    const percentages = [];

    for (const [genreId, count] of Object.entries(subscriber.genreScore)) {
      const genre = genres.find(g => g._id === genreId);
      if (genre) {
        percentages.push({
          name: genre.name,
          percentage: ((count / total) * 100).toFixed(1)
        });
      }
    }

    return percentages.sort((a, b) => b.percentage - a.percentage);
  };

  const resetFilters = () => {
    setSearch('');
    setSortBy('createdAt');
    setSortOrder('desc');
    setGenreFilter('');
    setGenreScoreThreshold(0);
    setMinDownloads(0);
    setCurrentPage(1);
  };

  const handleSubscriberClick = async (subscriber) => {
    if (editingId === subscriber._id) return; // Don't open modal when editing

    setShowDetailsModal(true);
    setLoadingDetails(true);

    try {
      const response = await api.get(`/subscribers/${subscriber._id}/details`);
      setSubscriberDetails(response.data.data);
    } catch (error) {
      console.error('Error fetching subscriber details:', error);
      setMessage('Error loading subscriber details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSubscriberDetails(null);
  };

  const handleExportCSV = () => {
    const params = new URLSearchParams({
      search,
      sortBy,
      sortOrder,
      ...(genreFilter && { genreFilter }),
      ...(genreScoreThreshold > 0 && { genreScoreThreshold }),
      ...(minDownloads > 0 && { minDownloads })
    });

    // Create a link to download the CSV
    const token = localStorage.getItem('token');
    const url = `${api.defaults.baseURL}/subscribers/export-csv?${params}`;
    
    // Create a temporary link element
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `subscribers-${new Date().toISOString().split('T')[0]}.csv`);
    
    // Add authorization header by opening in new window
    fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => response.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `subscribers-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setMessage('CSV exported successfully');
    })
    .catch(error => {
      console.error('Error downloading CSV:', error);
      setMessage('Error downloading CSV');
    });
  };

  if (loading && subscribers.length === 0) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="subscribers-page">
      <div className="page-header">
        <div>
          <h2>Subscriber Management</h2>
          <p>{totalCount} total subscribers</p>
        </div>
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'subscribers' ? 'active' : ''}`}
            onClick={() => setActiveTab('subscribers')}
          >
            Subscribers
          </button>
          <button 
            className={`tab ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <BarChart3 size={16} /> Analytics
          </button>
        </div>
      </div>

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      {activeTab === 'subscribers' && (
        <>
          <div className="controls-section">
            <div className="search-bar">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search by email, first name, or last name..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className="action-buttons">
              <button onClick={() => setShowFilters(!showFilters)} className="filter-btn">
                <Filter size={18} /> Filters
              </button>
              <button onClick={handleExportCSV} className="export-btn" title="Download subscriber data as CSV">
                <FileDown size={18} /> Export CSV
              </button>
              <button onClick={() => setShowAddForm(!showAddForm)} className="add-btn">
                <UserPlus size={18} /> Add Subscriber
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="filters-panel">
              <div className="filter-group">
                <label>Sort By</label>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="createdAt">Join Date</option>
                  <option value="email">Email</option>
                  <option value="totalDownloads">Downloads</option>
                  <option value="totalEmailsSent">Emails Sent</option>
                </select>
                <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Filter by Genre</label>
                <select value={genreFilter} onChange={(e) => setGenreFilter(e.target.value)}>
                  <option value="">All Genres</option>
                  {genres.map(genre => (
                    <option key={genre._id} value={genre._id}>{genre.name}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Genre Score Threshold: {genreScoreThreshold}%</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={genreScoreThreshold}
                  onChange={(e) => setGenreScoreThreshold(e.target.value)}
                  className="slider"
                />
              </div>

              <div className="filter-group">
                <label>Minimum Downloads</label>
                <input
                  type="number"
                  min="0"
                  value={minDownloads}
                  onChange={(e) => setMinDownloads(e.target.value)}
                  placeholder="0"
                />
              </div>

              <button onClick={resetFilters} className="reset-btn">Reset Filters</button>
            </div>
          )}

          {showAddForm && (
            <div className="add-form-card">
              <h3>Add New Subscriber</h3>
              <form onSubmit={handleAdd} className="add-form">
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Email *"
                  required
                />
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="First Name"
                />
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  placeholder="Last Name"
                />
                <div className="genre-selection">
                  <div className="genre-selection-header">
                    <label>Preferred Genres (Optional):</label>
                    <button 
                      type="button" 
                      className="select-all-genres-btn"
                      onClick={() => toggleAllGenres(false)}
                    >
                      {formData.preferredGenres?.length === genres.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="genre-chips">
                    {genres.map(genre => (
                      <div
                        key={genre._id}
                        className={`genre-chip ${formData.preferredGenres?.includes(genre._id) ? 'selected' : ''}`}
                        onClick={() => toggleGenreSelection(genre._id, false)}
                      >
                        {genre.name}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="form-actions">
                  <button type="submit" className="submit-btn">Add Subscriber</button>
                  <button type="button" onClick={() => setShowAddForm(false)} className="cancel-btn">
                    Cancel
                  </button>
                </div>
              </form>

              <div className="bulk-import">
                <div className="bulk-mode-switch">
                  <h4>📋 Bulk Text Import</h4>
                  <div className="switch-container">
                    <label className="switch-label">
                      <span className={bulkMode === 'subscribers' ? 'active' : ''}>Subscribers</span>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={bulkMode === 'genres'}
                          onChange={(e) => setBulkMode(e.target.checked ? 'genres' : 'subscribers')}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                      <span className={bulkMode === 'genres' ? 'active' : ''}>Genres</span>
                    </label>
                  </div>
                </div>

                {bulkMode === 'subscribers' ? (
                  <>
                    <p>Format: email|firstName|lastName|genre (one per line, pipe delimited)</p>
                    <textarea
                      value={bulkEmails}
                      onChange={(e) => setBulkEmails(e.target.value)}
                      placeholder="john@example.com|John|Doe|Techno&#10;jane@example.com|Jane|Smith|House"
                      rows={5}
                    />
                  </>
                ) : (
                  <>
                    <p>Enter genre names (one per line). Existing genres will be skipped.</p>
                    <textarea
                      value={bulkGenres}
                      onChange={(e) => setBulkGenres(e.target.value)}
                      placeholder="Techno&#10;House&#10;Trance&#10;Progressive House&#10;Deep House"
                      rows={5}
                    />
                  </>
                )}
                <button onClick={handleBulkImport} className="import-btn">
                  <Upload size={16} /> Import {bulkMode === 'genres' ? 'Genres' : 'Subscribers'}
                </button>
              </div>

              <div className="csv-import">
                <h4>📄 CSV Import</h4>
                <p>Upload a CSV with columns: email, firstName, lastName, genre</p>
                <div className="csv-upload-area">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCsvFile(e.target.files[0])}
                    id="csv-upload"
                  />
                  <label htmlFor="csv-upload" className="csv-label">
                    <Download size={20} />
                    {csvFile ? csvFile.name : 'Choose CSV file'}
                  </label>
                  {csvFile && (
                    <button onClick={handleCSVUpload} className="import-btn">
                      <Upload size={16} /> Import CSV
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="subscribers-list">
            {subscribers.map((subscriber) => (
              <div 
                key={subscriber._id} 
                className={`subscriber-card ${editingId !== subscriber._id ? 'clickable' : ''}`}
                onClick={() => editingId !== subscriber._id && handleSubscriberClick(subscriber)}
              >
                {editingId === subscriber._id ? (
                  <div className="edit-form">
                    <input
                      type="text"
                      value={editData.firstName}
                      onChange={(e) => setEditData({ ...editData, firstName: e.target.value })}
                      placeholder="First Name"
                    />
                    <input
                      type="text"
                      value={editData.lastName}
                      onChange={(e) => setEditData({ ...editData, lastName: e.target.value })}
                      placeholder="Last Name"
                    />
                    <div className="genre-selection">
                      <div className="genre-selection-header">
                        <label>Preferred Genres:</label>
                        <button 
                          type="button" 
                          className="select-all-genres-btn"
                          onClick={() => toggleAllGenres(true)}
                        >
                          {editData.preferredGenres?.length === genres.length ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>
                      <div className="genre-chips">
                        {genres.map(genre => (
                          <div
                            key={genre._id}
                            className={`genre-chip ${editData.preferredGenres?.includes(genre._id) ? 'selected' : ''}`}
                            onClick={() => toggleGenreSelection(genre._id, true)}
                          >
                            {genre.name}
                          </div>
                        ))}
                      </div>
                    </div>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={editData.isActive}
                        onChange={(e) => setEditData({ ...editData, isActive: e.target.checked })}
                      />
                      Active
                    </label>
                    <div className="edit-actions">
                      <button onClick={() => handleSave(subscriber._id)} className="save-btn">
                        <Save size={16} /> Save
                      </button>
                      <button onClick={() => setEditingId(null)} className="cancel-btn-inline">
                        <X size={16} /> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="subscriber-info">
                      <div className="subscriber-header">
                        <h3>{subscriber.email}</h3>
                        <span className={`status ${subscriber.isActive ? 'active' : 'inactive'}`}>
                          {subscriber.isActive ? '✅ Active' : '❌ Inactive'}
                        </span>
                      </div>
                      {subscriber.name && (
                        <p className="subscriber-name">
                          <strong>Name:</strong> {subscriber.name}
                        </p>
                      )}
                      {(subscriber.firstName || subscriber.lastName) && (
                        <p className="subscriber-name">
                          {subscriber.firstName} {subscriber.lastName}
                        </p>
                      )}
                      {subscriber.preferredGenres && subscriber.preferredGenres.length > 0 && (
                        <div className="preferred-genres">
                          <strong>Preferred Genres:</strong>
                          {subscriber.preferredGenres.map(genre => (
                            <span key={genre._id} className="genre-badge">{genre.name}</span>
                          ))}
                        </div>
                      )}
                      {getGenreScorePercentages(subscriber).length > 0 && (
                        <div className="genre-scores">
                          <strong>Genre Scores:</strong>
                          {getGenreScorePercentages(subscriber).map(score => (
                            <span key={score.name} className="score-badge">
                              {score.name}: {score.percentage}%
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="subscriber-stats">
                        <span>📧 {subscriber.totalEmailsSent || 0} emails</span>
                        <span>⬇️ {subscriber.totalDownloads || 0} downloads</span>
                        <span>📅 {new Date(subscriber.subscribedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="subscriber-actions" onClick={(e) => e.stopPropagation()}>
                      <button onClick={(e) => { e.stopPropagation(); handleEdit(subscriber); }} className="edit-btn">
                        <Edit2 size={18} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(subscriber._id); }} className="delete-btn">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="page-btn"
              >
                Previous
              </button>
              
              <div className="page-numbers">
                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 2 && page <= currentPage + 2)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`page-number ${currentPage === page ? 'active' : ''}`}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 3 || page === currentPage + 3) {
                    return <span key={page} className="page-ellipsis">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="page-btn"
              >
                Next
              </button>

              <span className="page-info">
                Page {currentPage} of {totalPages} ({totalCount} total)
              </span>
            </div>
          )}

          {subscribers.length === 0 && !loading && (
            <div className="empty-state">
              <UserPlus size={64} />
              <p>No subscribers found. Try adjusting your filters or add some subscribers!</p>
            </div>
          )}
        </>
      )}

      {activeTab === 'analytics' && (
        <div className="analytics-tab">
          {analytics ? (
            <>
              <div className="analytics-section">
                <h3>Overview</h3>
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-value">{analytics.overview.totalSubscribers}</div>
                    <div className="stat-label">Total Subscribers</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{analytics.overview.activeSubscribers}</div>
                    <div className="stat-label">Active</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{analytics.overview.totalDownloads}</div>
                    <div className="stat-label">Total Downloads</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{analytics.overview.totalListens}</div>
                    <div className="stat-label">Total Listens</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{analytics.overview.listenRate}</div>
                    <div className="stat-label">Listen Rate</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{analytics.overview.downloadRate}</div>
                    <div className="stat-label">Download Rate</div>
                  </div>
                </div>
              </div>

              <div className="analytics-section">
                <h3>Top Downloaders</h3>
                <div className="table-container">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Name</th>
                        <th>Downloads</th>
                        <th>Emails Sent</th>
                        <th>Engagement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.topDownloaders.map((sub) => (
                        <tr key={sub._id}>
                          <td>{sub.email}</td>
                          <td>{sub.firstName && sub.lastName ? `${sub.firstName} ${sub.lastName}` : '-'}</td>
                          <td><strong>{sub.totalDownloads}</strong></td>
                          <td>{sub.totalEmailsSent}</td>
                          <td>{sub.totalEmailsSent > 0 ? ((sub.totalDownloads / sub.totalEmailsSent) * 100).toFixed(1) : 0}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="analytics-section">
                <h3>Genre Preferences</h3>
                <div className="table-container">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Genre</th>
                        <th>Download Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.genrePreferences.map((pref) => (
                        <tr key={pref._id}>
                          <td><strong>{pref.genreName}</strong></td>
                          <td>{pref.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="analytics-section">
                <h3>Category Preferences</h3>
                <div className="table-container">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Download Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.categoryPreferences.map((pref) => (
                        <tr key={pref._id}>
                          <td><strong>{pref.categoryName}</strong></td>
                          <td>{pref.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="analytics-section">
                <h3>Most Engaged Subscribers</h3>
                <div className="table-container">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Name</th>
                        <th>Engagement Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.engagedSubscribers.map((sub) => (
                        <tr key={sub._id}>
                          <td>{sub.email}</td>
                          <td>{sub.firstName && sub.lastName ? `${sub.firstName} ${sub.lastName}` : '-'}</td>
                          <td><strong>{sub.engagementRate}%</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="loading">Loading analytics...</div>
          )}
        </div>
      )}

      {/* Subscriber Details Modal */}
      {showDetailsModal && (
        <div className="modal-overlay" onClick={closeDetailsModal}>
          <div className="modal-content details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Subscriber Details</h2>
              <button onClick={closeDetailsModal} className="close-btn">
                <X size={24} />
              </button>
            </div>

            {loadingDetails ? (
              <div className="modal-loading">Loading...</div>
            ) : subscriberDetails ? (
              <div className="details-content">
                <div className="details-info">
                  <h3>{subscriberDetails.subscriber.email}</h3>
                  {subscriberDetails.subscriber.name && (
                    <p className="subscriber-full-name">
                      <strong>Artist Name:</strong> {subscriberDetails.subscriber.name}
                    </p>
                  )}
                  {(subscriberDetails.subscriber.firstName || subscriberDetails.subscriber.lastName) && (
                    <p className="subscriber-full-name">
                      {subscriberDetails.subscriber.firstName} {subscriberDetails.subscriber.lastName}
                    </p>
                  )}
                  
                  {/* Genre and Country */}
                  {(subscriberDetails.subscriber.genre || subscriberDetails.subscriber.country) && (
                    <div className="subscriber-meta">
                      {subscriberDetails.subscriber.genre && (
                        <p><strong>Genre:</strong> {subscriberDetails.subscriber.genre}
                        {subscriberDetails.subscriber.subGenre && ` / ${subscriberDetails.subscriber.subGenre}`}</p>
                      )}
                      {subscriberDetails.subscriber.country && (
                        <p><strong>Country:</strong> {subscriberDetails.subscriber.country}</p>
                      )}
                    </div>
                  )}

                  {/* Social Media Links */}
                  {subscriberDetails.subscriber.socialMediaLinks && subscriberDetails.subscriber.socialMediaLinks.length > 0 && (
                    <div className="details-social-links">
                      <strong>Social Media:</strong>
                      <div className="social-links-grid">
                        {subscriberDetails.subscriber.socialMediaLinks.map((link, idx) => (
                          <a 
                            key={idx} 
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="social-link-badge"
                          >
                            {link.platform}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Artist Links */}
                  {(subscriberDetails.subscriber.labelRadarLink || subscriberDetails.subscriber.spotifyArtistLink) && (
                    <div className="details-artist-links">
                      <strong>Artist Profiles:</strong>
                      <div className="artist-links-grid">
                        {subscriberDetails.subscriber.labelRadarLink && (
                          <a 
                            href={subscriberDetails.subscriber.labelRadarLink} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="artist-link"
                          >
                            🎵 LabelRadar
                          </a>
                        )}
                        {subscriberDetails.subscriber.spotifyArtistLink && (
                          <a 
                            href={subscriberDetails.subscriber.spotifyArtistLink} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="artist-link"
                          >
                            🎧 Spotify
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="details-stats-grid">
                  <div className="detail-stat-card">
                    <div className="stat-icon">📧</div>
                    <div className="stat-number">{subscriberDetails.stats.totalSent}</div>
                    <div className="stat-name">Emails Sent</div>
                  </div>
                  <div className="detail-stat-card">
                    <div className="stat-icon">👂</div>
                    <div className="stat-number">{subscriberDetails.stats.totalListens}</div>
                    <div className="stat-name">Listens</div>
                  </div>
                  <div className="detail-stat-card">
                    <div className="stat-icon">⬇️</div>
                    <div className="stat-number">{subscriberDetails.stats.totalDownloads}</div>
                    <div className="stat-name">Downloads</div>
                  </div>
                  <div className="detail-stat-card">
                    <div className="stat-icon">🎵</div>
                    <div className="stat-number">{subscriberDetails.stats.downloadedGenres.length}</div>
                    <div className="stat-name">Genres</div>
                  </div>
                </div>

                {subscriberDetails.stats.downloadedGenres.length > 0 && (
                  <div className="details-genres">
                    <strong>Downloaded Genres:</strong>
                    <div className="genre-tags">
                      {subscriberDetails.stats.downloadedGenres.map((genre, idx) => (
                        <span key={idx} className="genre-tag">{genre}</span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="samples-history">
                  <h4>Sent Samples History ({subscriberDetails.sentSamples.length})</h4>
                  <div className="samples-table-container">
                    <table className="samples-history-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Sample Name</th>
                          <th>Category</th>
                          <th>Genre</th>
                          <th>Listened</th>
                          <th>Downloaded</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscriberDetails.sentSamples.map((sample) => (
                          <tr key={sample._id}>
                            <td>{new Date(sample.sentAt).toLocaleDateString()}</td>
                            <td className="sample-name">{sample.sampleName}</td>
                            <td><span className="category-badge">{sample.category}</span></td>
                            <td><span className="genre-badge">{sample.genre}</span></td>
                            <td className="status-cell">
                              {sample.listened ? (
                                <span className="status-yes">✓ Yes</span>
                              ) : (
                                <span className="status-no">✗ No</span>
                              )}
                            </td>
                            <td className="status-cell">
                              {sample.downloaded ? (
                                <span className="status-yes">✓ Yes</span>
                              ) : (
                                <span className="status-no">✗ No</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {subscriberDetails.sentSamples.length === 0 && (
                    <p className="no-data">No samples sent to this subscriber yet.</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="modal-error">Failed to load details</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscribers;
